import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import ConfirmModal from "../components/ConfirmModal";
import HousingHistoryTable from "../components/application/HousingHistoryTable";
import HousingHistoryForm from "../components/application/HousingHistoryForm";
import {
  getHousingEntries,
  createHousingEntry,
  updateHousingEntry,
  deleteHousingEntry,
  ApplicationHousingEntryDto,
  ApplicationHousingEntryFormInput,
} from "../api/services";

const HousingHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const { id: applicationId } = useParams<{ id: string }>();

  const [entries, setEntries] = useState<ApplicationHousingEntryDto[]>([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ApplicationHousingEntryDto | null>(
    null
  );

  const [deleting, setDeleting] = useState<ApplicationHousingEntryDto | null>(
    null
  );

  const fetchEntries = () => {
    if (!applicationId) return;
    setLoading(true);
    getHousingEntries(applicationId)
      .then((res) => setEntries(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEntries();
  }, [applicationId]);

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const handleEdit = (entry: ApplicationHousingEntryDto) => {
    setEditing(entry);
    setShowForm(true);
  };
  const handleDelete = (entry: ApplicationHousingEntryDto) => {
    setDeleting(entry);
  };

  const confirmDelete = () => {
    if (!applicationId || !deleting) return;
    deleteHousingEntry(applicationId, deleting.id).then(() => {
      setDeleting(null);
      fetchEntries();
    });
  };
  const cancelDelete = () => setDeleting(null);

  const handleSave = (data: ApplicationHousingEntryFormInput) => {
    if (!applicationId) return;
    const fn = editing
      ? updateHousingEntry(applicationId, editing.id, data)
      : createHousingEntry(applicationId, data);

    fn.then(() => {
      setShowForm(false);
      fetchEntries();
    });
  };

  if (loading) {
    return (
      <div className="mt-6">
        <GoBack />
        <p>{t("housingHistory.loading", "Loading...")}</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <GoBack />
      <h2 className="mb-2">{t("housingHistory.title")}</h2>
      <p className="mb-6">{t("housingHistory.intro")}</p>

      {entries.length > 0 && (
        <HousingHistoryTable
          entries={entries}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <div className="mb-6">
        <button className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light" onClick={handleAdd}>
          {t("housingHistory.buttons.addAnother")}
        </button>
      </div>

      {showForm && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="font-semibold text-primary">
                {editing
                  ? t("housingHistory.editTitle", "Edit Housing Entry")
                  : t("housingHistory.addTitle", "Add Housing Entry")}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
                onClick={() => setShowForm(false)}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              <HousingHistoryForm
                initial={editing ?? undefined}
                onSave={handleSave}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleting}
        message={t("housingHistory.confirmDelete")}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default HousingHistoryPage;
